import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconDeviceMobile } from '@tabler/icons-react';
import { QRCodeSVG } from 'qrcode.react';

import { TOUR_SHEET_COPY } from './tourBookingSheetCopy';

const QR_SIZE = 124;

const StatusIcons = () => (
  <Row className="w-12 shrink-0 items-center justify-end gap-1">
    <svg width="16" height="10" viewBox="0 0 16 10" className="text-od-content-inverse-primary">
      <rect x="0" y="7" width="3" height="3" rx="0.5" fill="currentColor" />
      <rect x="4.3" y="5" width="3" height="5" rx="0.5" fill="currentColor" />
      <rect x="8.6" y="2.5" width="3" height="7.5" rx="0.5" fill="currentColor" />
      <rect x="12.9" y="0" width="3" height="10" rx="0.5" fill="currentColor" />
    </svg>
    <svg width="20" height="10" viewBox="0 0 20 10">
      <rect
        x="0.5"
        y="0.5"
        width="16"
        height="9"
        rx="2.5"
        fill="none"
        stroke="var(--color-od-content-inverse-secondary)"
      />
      <rect
        x="2"
        y="2"
        width="11"
        height="6"
        rx="1.2"
        fill="var(--color-od-content-inverse-primary)"
      />
      <rect
        x="18"
        y="3.5"
        width="1.5"
        height="3"
        rx="0.75"
        fill="var(--color-od-content-inverse-secondary)"
      />
    </svg>
  </Row>
);

const ViewfinderBrackets = () => (
  <svg
    width="168"
    height="168"
    viewBox="0 0 168 168"
    aria-hidden
    className="absolute inset-0 text-amber-400"
  >
    <path
      d="M2 26V14C2 7.4 7.4 2 14 2H26M142 2H154C160.6 2 166 7.4 166 14V26M166 142V154C166 160.6 160.6 166 154 166H142M26 166H14C7.4 166 2 160.6 2 154V142"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const ScanPill = () => (
  <Row aria-hidden className="shrink-0 items-center gap-2 rounded-full bg-amber-400 py-2 pr-4 pl-2">
    <Box className="size-5 shrink-0 items-center justify-center rounded-full bg-od-bg-accent-brand">
      <IconDeviceMobile size={16} stroke={1.333} className="text-white" />
    </Box>
    <Text className="shrink-0 text-label-sm whitespace-nowrap text-od-content-primary">
      {TOUR_SHEET_COPY.confirmation.scanPill}
    </Text>
  </Row>
);

/**
 * The desktop confirmation's app handoff: the download QR framed by an iPhone
 * camera viewfinder, so the scan reads as "point your phone here". Only the QR
 * is exposed to assistive tech; the phone around it is decoration.
 */
export const AppHandoffPhone = ({ qrValue }: { qrValue: string }) => (
  <Row className="h-88 shrink-0 justify-center overflow-hidden bg-od-bg-tertiary pt-10">
    <Box className="relative h-100 w-60 shrink-0 rounded-4xl bg-od-bg-inverse-primary p-2 shadow-[0_20px_40px_-12px_rgb(0_0_0/0.2)]">
      <Box
        aria-hidden
        className="absolute top-18 -left-0.75 h-5 w-0.75 rounded-xs bg-od-bg-inverse-primary"
      />
      <Box
        aria-hidden
        className="absolute top-27 -left-0.75 h-9 w-0.75 rounded-xs bg-od-bg-inverse-primary"
      />
      <Box
        aria-hidden
        className="absolute top-38 -left-0.75 h-9 w-0.75 rounded-xs bg-od-bg-inverse-primary"
      />
      <Box
        aria-hidden
        className="absolute top-30 -right-0.75 h-14 w-0.75 rounded-xs bg-od-bg-inverse-primary"
      />
      <Col className="size-full items-center overflow-hidden rounded-3xl bg-neutral-800 px-5 pt-3">
        <Row aria-hidden className="h-6 w-full shrink-0 items-center justify-between">
          <Text className="w-12 shrink-0 text-label-sm text-od-content-inverse-primary">9:41</Text>
          <Box className="h-5.5 w-18 shrink-0 rounded-full bg-black" />
          <StatusIcons />
        </Row>
        <Col className="items-center gap-5 pt-6">
          <Box className="relative size-42 shrink-0 items-center justify-center">
            <Box
              className="rounded-lg bg-od-bg-primary p-2"
              aria-label="QR code to download the Opendoor app"
              role="img"
            >
              <QRCodeSVG value={qrValue} size={QR_SIZE} />
            </Box>
            <ViewfinderBrackets />
          </Box>
          <ScanPill />
        </Col>
      </Col>
    </Box>
  </Row>
);
